import { createAsyncThunk } from '@reduxjs/toolkit'
import { commentAdded, commentDeleted } from './predictionMarket.slice'
import { getCategoriesService, getMarketsService, getMarketDetailsService, placeOrderService,
  getOptionDetailsService, getOptionGraphDataService, getMyPositionsService, depositService, withdrawService, getMyOrdersService,
  getTopHoldersService, getMarketCommentsService, getMyMarketCommentsService, deleteCommentService,
  likeToggleService, addCommentService, cancelLimitOrderService,
  getMyOptionPositionService, getOptionOrderbookService, getHistoryService, getMyProfitService, getMarketActivityService,
  getTopPositionsService, toggleFavouriteQuestionService, getNewsService } from '../predictionMarket.service'
import { ORDER_TYPES, ORDER_STATES } from '../predictionMarket.constants'
import { openErrorToaster, openSuccessToaster } from 'helpers/toaster.helpers'
import { getTranslation } from 'helpers/translations.helpers'
import {
  demoGetCategories,
  demoGetQuestionDetails,
  demoGetOptionGraphData,
  demoGetOptionOrderbook,
  demoGetMyOptionPosition,
  demoGetMyOrders,
  demoGetMarketComments,
  demoGetMarketActivity,
  demoGetTopHolders,
  demoGetTopPositions,
  demoPlaceOrder,
  demoCancelLimitOrder,
  demoAddComment,
  demoDeleteComment,
  demoLikeToggle,
  demoGetActiveMarketOrder,
  isDemoModeFromSearch,
  isDemoMarketId
} from '../demo.helpers'

const isDemoModeActive = (thunkApi, params = {}) => {
  if (thunkApi.getState().predictionMarket?.isDemoMode === true) return true
  if (isDemoMarketId(params?.questionId ?? params?.marketId)) return true
  if (typeof window !== 'undefined') {
    return isDemoModeFromSearch(window.location.search)
  }
  return false
}

export const getCategories = createAsyncThunk(
  'prediction-market/get-categories',
  async (params = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, params)) {
      return demoGetCategories()
    }
    try {
      const res = await getCategoriesService()
      return {
        categories: res?.categories || [],
        tree: res?.tree || []
      }
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getMarkets = createAsyncThunk(
  'prediction-market/get-markets',
  async (params, thunkApi) => {
    try {
      const res = await getMarketsService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getMarketDetails = createAsyncThunk(
  'prediction-market/get-market-details',
  async (params = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, params)) {
      return demoGetQuestionDetails(params)
    }
    try {
      const res = await getMarketDetailsService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const placeOrder = createAsyncThunk(
  'prediction-market/place-order',
  async (payload = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, payload)) {
      const res = demoPlaceOrder(payload, thunkApi)
      if (res.success) {
        if (payload?.type === ORDER_TYPES.MERGE) {
          openSuccessToaster({
            message: res.credited
              ? getTranslation('mergeSuccessfulBalanceCredited')
              : getTranslation('mergeSuccessfulCreditPending')
          })
        } else {
          openSuccessToaster({ message: getTranslation('orderPlacedSuccessfully') })
        }
      } else {
        openErrorToaster({ message: getTranslation('placeOrderFailedPleaseTryAgain') })
        return thunkApi.rejectWithValue(getTranslation('placeOrderFailedPleaseTryAgain'))
      }
      return res
    }
    try {
      const res = await placeOrderService(payload)
      if (res.success) {
        if (payload?.type === ORDER_TYPES.MERGE) {
          openSuccessToaster({
            message: res.credited
              ? getTranslation('mergeSuccessfulBalanceCredited')
              : getTranslation('mergeSuccessfulCreditPending')
          })
        } else {
          openSuccessToaster({ message: getTranslation('orderPlacedSuccessfully') })
        }
        return res
      }

      openErrorToaster({ message: getTranslation('placeOrderFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(getTranslation('placeOrderFailedPleaseTryAgain'))
    } catch (error) {
      console.log(error)
      openErrorToaster({ message: getTranslation('placeOrderFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const cancelLimitOrder = createAsyncThunk(
  'prediction-market/cancel-limit-order',
  async (payload = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, payload)) {
      const res = demoCancelLimitOrder(payload, thunkApi)
      if (res.success) {
        openSuccessToaster({ message: getTranslation('orderCancelledSuccessfully') })
        return res
      }
      openErrorToaster({ message: getTranslation('cancelLimitOrderFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(getTranslation('cancelLimitOrderFailedPleaseTryAgain'))
    }
    try {
      const res = await cancelLimitOrderService(payload)

      if (res.success) {
        openSuccessToaster({ message: getTranslation('orderCancelledSuccessfully') })
        return res
      }

      openErrorToaster({ message: getTranslation('cancelLimitOrderFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(getTranslation('cancelLimitOrderFailedPleaseTryAgain'))
    } catch (error) {
      console.log(error)
      openErrorToaster({ message: getTranslation('cancelLimitOrderFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getOptionDetails = createAsyncThunk(
  'prediction-market/get-option-details',
  async (params, thunkApi) => {
    try {
      const res = await getOptionDetailsService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getOptionGraphData = createAsyncThunk(
  'prediction-market/get-option-graph-data',
  async (params, thunkApi) => {
    if (isDemoModeActive(thunkApi)) {
      return demoGetOptionGraphData(params)
    }
    try {
      const res = await getOptionGraphDataService(params)
      return { optionId: params.optionId, ...res }
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0]?.description)
    }
  }
)

export const getOptionOrderbook = createAsyncThunk(
  'prediction-market/get-option-orderbook',
  async (params = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi)) {
      return demoGetOptionOrderbook(params, thunkApi.getState)
    }
    try {
      const res = await getOptionOrderbookService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getMyPositions = createAsyncThunk(
  'prediction-market/get-my-positions',
  async (params, thunkApi) => {
    try {
      const res = await getMyPositionsService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getMyOrders = createAsyncThunk(
  'prediction-market/get-my-orders',
  async (params = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, params)) {
      return demoGetMyOrders(params)
    }
    try {
      const res = await getMyOrdersService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getMyOptionOrders = createAsyncThunk(
  'prediction-market/get-my-option-orders',
  async (params = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi)) {
      return demoGetMyOrders({ ...params, retrieveAll: true })
    }
    try {
      const res = await getMyOrdersService({ ...params, retrieveAll: true })
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getActiveMarketOrder = createAsyncThunk(
  'prediction-market/get-active-market-order',
  async (params = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, params)) {
      return demoGetActiveMarketOrder(thunkApi.getState)
    }
    try {
      const res = await getMyOrdersService({
        orderType: ORDER_TYPES.MARKET,
        state: ORDER_STATES.OPEN,
        limit: 1,
        page: 1
      })
      return res.rows?.[0] ?? null
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0]?.description)
    }
  }
)

export const getHistory = createAsyncThunk(
  'prediction-market/get-history',
  async (params, thunkApi) => {
    try {
      const res = await getHistoryService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const loadNotifications = createAsyncThunk(
  'prediction-market/load-notifications',
  async (params = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, params)) {
      return { skipped: true }
    }
    const { notifications, history } = thunkApi.getState().predictionMarket

    if (notifications.items.length > 0 || history.rows.length > 0) {
      return { skipped: true }
    }

    try {
      const res = await getHistoryService({ page: 1, limit: 20 })
      return res
    } catch (error) {
      return thunkApi.rejectWithValue(error?.[0]?.description ?? getTranslation('failedToLoadNotifications'))
    }
  }
)

export const getMarketActivity = createAsyncThunk(
  'prediction-market/get-market-activity',
  async (params, thunkApi) => {
    if (isDemoModeActive(thunkApi)) {
      return demoGetMarketActivity(params)
    }
    try {
      const res = await getMarketActivityService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getMyProfit = createAsyncThunk(
  'prediction-market/get-my-profit',
  async (params, thunkApi) => {
    try {
      const res = await getMyProfitService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getTopHolders = createAsyncThunk(
  'prediction-market/get-top-holders',
  async (params, thunkApi) => {
    if (isDemoModeActive(thunkApi)) {
      return demoGetTopHolders(params)
    }
    try {
      const res = await getTopHoldersService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getTopPositions = createAsyncThunk(
  'prediction-market/get-top-positions',
  async (params, thunkApi) => {
    if (isDemoModeActive(thunkApi)) {
      return demoGetTopPositions(params)
    }
    try {
      const res = await getTopPositionsService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getMarketComments = createAsyncThunk(
  'prediction-market/get-market-comments',
  async (params, thunkApi) => {
    if (isDemoModeActive(thunkApi)) {
      return demoGetMarketComments(params)
    }
    try {
      const res = await getMarketCommentsService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getMyOptionPosition = createAsyncThunk(
  'prediction-market/get-my-option-position',
  async (params, thunkApi) => {
    if (isDemoModeActive(thunkApi)) {
      return demoGetMyOptionPosition(params)
    }
    try {
      const res = await getMyOptionPositionService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const getMyMarketComments = createAsyncThunk(
  'prediction-market/get-my-market-comments',
  async (params, thunkApi) => {
    try {
      const res = await getMyMarketCommentsService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const addComment = createAsyncThunk(
  'prediction-market/add-comment',
  async (payload = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, payload)) {
      const res = demoAddComment(payload)
      if (res.success) {
        thunkApi.dispatch(commentAdded(res.comment))
        openSuccessToaster({ message: getTranslation('commentAdded') })
        return res
      }
      openErrorToaster({ message: res.error ?? getTranslation('addCommentFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(res.error ?? getTranslation('addCommentFailedPleaseTryAgain'))
    }
    try {
      const res = await addCommentService(payload)
      if (res.success) {
        openSuccessToaster({ message: getTranslation('commentAdded') })
        return res
      }

      openErrorToaster({ message: getTranslation('addCommentFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(getTranslation('addCommentFailedPleaseTryAgain'))
    } catch (error) {
      console.log(error)
      openErrorToaster({ message: getTranslation('addCommentFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const deleteComment = createAsyncThunk(
  'prediction-market/delete-comment',
  async (payload = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, payload)) {
      const res = demoDeleteComment(payload)
      if (res.success) {
        thunkApi.dispatch(commentDeleted({ commentId: res.commentId, parentCommentId: res.parentCommentId }))
        openSuccessToaster({ message: getTranslation('commentDeleted') })
        return res
      }
      openErrorToaster({ message: res.error ?? getTranslation('deleteCommentFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(res.error ?? getTranslation('deleteCommentFailedPleaseTryAgain'))
    }
    try {
      const res = await deleteCommentService(payload)
      if (res.success) {
        openSuccessToaster({ message: getTranslation('commentDeleted') })
        return res
      }

      openErrorToaster({ message: getTranslation('deleteCommentFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(getTranslation('deleteCommentFailedPleaseTryAgain'))
    } catch (error) {
      console.log(error)
      openErrorToaster({ message: getTranslation('deleteCommentFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const likeToggle = createAsyncThunk(
  'prediction-market/like-toggle',
  async (payload = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, payload)) {
      return demoLikeToggle(payload)
    }
    try {
      const res = await likeToggleService(payload)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const deposit = createAsyncThunk(
  'prediction-market/deposit',
  async (payload, thunkApi) => {
    try {
      const res = await depositService(payload)
      if (res.success) {
        openSuccessToaster({ message: getTranslation('depositSuccessful') })
        return res
      }

      openErrorToaster({ message: getTranslation('depositFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(getTranslation('depositFailedPleaseTryAgain'))
    } catch (error) {
      console.log(error)
      openErrorToaster({ message: getTranslation('depositFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const withdraw = createAsyncThunk(
  'prediction-market/withdraw',
  async (payload, thunkApi) => {
    try {
      const res = await withdrawService(payload)
      if (res.success) {
        openSuccessToaster({ message: getTranslation('withdrawalSuccessful') })
        return res
      }

      openErrorToaster({ message: getTranslation('withdrawFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(getTranslation('withdrawFailedPleaseTryAgain'))
    } catch (error) {
      console.log(error)
      openErrorToaster({ message: getTranslation('withdrawFailedPleaseTryAgain') })
      return thunkApi.rejectWithValue(error[0].description)
    }
  }
)

export const toggleFavouriteQuestion = createAsyncThunk(
  'prediction-market/toggle-favourite-question',
  async (payload = {}, thunkApi) => {
    if (isDemoModeActive(thunkApi, payload)) {
      return { success: true, skipped: true }
    }
    try {
      const res = await toggleFavouriteQuestionService(payload)
      return res
    } catch (error) {
      console.log(error)
      openErrorToaster({ message: getTranslation('unableToUpdateFavouriteQuestionPleaseTryAgain') })
      return thunkApi.rejectWithValue(error[0]?.description)
    }
  }
)

export const getNews = createAsyncThunk(
  'prediction-market/get-news',
  async (params, thunkApi) => {
    try {
      const res = await getNewsService(params)
      return res
    } catch (error) {
      console.log(error)
      return thunkApi.rejectWithValue(error[0]?.description)
    }
  }
)
